import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import api from "./api";
import { DAYS, CITIES, STATS, tf, ts, GOLD } from "./data";

// ===========================================================================
// Default content (used until server content loads, and as fallback shape)
// ===========================================================================
const DEFAULT_HERO = {
  eyebrow: "Private Travel Journal · May 2026",
  line1: "Shah Family",
  line2a: "Japan",
  line2b: "· 2026",
  subtitle: "A father and two sons · 13 nights · 4 May – 17 May 2026",
  subtitle_extra: "ANA NH830 direct · BOM → NRT",
  cta: "Explore the Journey",
};

const DEFAULT_FOOTER = {
  line: "Shah Family · Japan 2026 · A private journal",
  easter: "made with ♥ by Dad",
  secret: "— for the two boys who made it unforgettable",
};

const DEFAULT_SIGNATURES = [
  {
    name: "Dad",
    role: "Tour leader · photographer · sleep-deprived",
    quote:
      "Watching the boys' faces at teamLab Planets and on the Mario Kart AR ride was worth the whole trip. 13 nights, one lifetime memory.",
    favourite: "Mt Fuji from Chureito Pagoda · sunrise clarity",
    signature: "— Dad",
    accent: "#1B2A4A",
  },
  {
    name: "Elder Son",
    role: "Camera operator · Nintendo World champion",
    quote:
      "Mario Kart with AR headsets was unreal — I've never been more in a video game. Also got the Canon R10 at Edion. Best two weeks ever.",
    favourite: "Nintendo World — Mario Kart AR · Day 12",
    signature: "— Elder Son",
    accent: "#1A6B6B",
  },
  {
    name: "Younger Son",
    role: "Snack curator · deer-whisperer",
    quote:
      "The deer in Nara actually bow when you hold a senbei cracker. We must have fed thirty of them. Also butterbeer is real.",
    favourite: "Nara deer · Day 10 · ∞ crackers",
    signature: "— Younger Son",
    accent: "#B5302A",
  },
];

const DEFAULT_CONTENT = {
  hero: DEFAULT_HERO,
  footer: DEFAULT_FOOTER,
  stats: STATS,
  cities: CITIES,
  days: DAYS,
  signatures: DEFAULT_SIGNATURES,
};

// ===========================================================================
// Context
// ===========================================================================
const ContentCtx = createContext(null);
export const useContent = () => useContext(ContentCtx);

export function ContentProvider({ children, isAdmin }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});

  // Load once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        if (data && Object.keys(data).length) {
          setContent((c) => ({
            ...c,
            ...data,
            hero: { ...c.hero, ...(data.hero || {}) },
            footer: { ...c.footer, ...(data.footer || {}) },
          }));
        }
      } catch (e) {
        console.warn("Could not load content from server, using defaults:", e.message);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // Debounced server save
  const queueSave = useCallback(
    (patch) => {
      pendingPatch.current = { ...pendingPatch.current, ...patch };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        const body = pendingPatch.current;
        pendingPatch.current = {};
        if (!Object.keys(body).length) return;
        setSaving(true);
        try {
          await api.put("/content", body);
        } catch (e) {
          console.error("Failed to save content:", e?.response?.data?.detail || e.message);
          alert("Save failed: " + (e?.response?.data?.detail || e.message));
        } finally {
          setSaving(false);
        }
      }, 600);
    },
    []
  );

  // Update a value at path: e.g. updateField(['hero','line1'], 'New Title')
  const updateField = useCallback(
    (path, value) => {
      setContent((prev) => {
        const next = structuredClone(prev);
        let cursor = next;
        for (let i = 0; i < path.length - 1; i++) cursor = cursor[path[i]];
        cursor[path[path.length - 1]] = value;
        // Save the top-level section that changed
        queueSave({ [path[0]]: next[path[0]] });
        return next;
      });
    },
    [queueSave]
  );

  // Reorder helper for an array section (e.g. 'days', 'cities', 'signatures')
  const move = useCallback(
    (section, fromIdx, toIdx) => {
      setContent((prev) => {
        const list = prev[section];
        if (!list || toIdx < 0 || toIdx >= list.length) return prev;
        const next = [...list];
        const [item] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, item);
        const updated = { ...prev, [section]: next };
        queueSave({ [section]: next });
        return updated;
      });
    },
    [queueSave]
  );

  return (
    <ContentCtx.Provider
      value={{ content, loaded, saving, isAdmin, updateField, move }}
    >
      {children}
      {isAdmin && saving && (
        <div className="jp-save-indicator" data-testid="save-indicator">
          Saving…
        </div>
      )}
    </ContentCtx.Provider>
  );
}

// ===========================================================================
// EditableText — renders text; admins see pencil → click to edit inline.
// path = ["hero","line1"]   for object fields
// path = ["days", 0, "title"]   for nested array fields
// ===========================================================================
export function EditableText({
  path,
  value,
  multiline = false,
  placeholder = "",
  className = "",
  style,
  as: Tag = "span",
  testId,
}) {
  const ctx = useContent();
  const { isAdmin } = ctx || {};
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try {
        inputRef.current.select();
      } catch (_) {}
    }
  }, [editing]);

  const commit = () => {
    if (draft !== value) ctx.updateField(path, draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  if (!isAdmin) {
    return (
      <Tag className={className} style={style} data-testid={testId}>
        {value}
      </Tag>
    );
  }

  if (editing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          className="jp-edit-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Escape") cancel();
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
          }}
          placeholder={placeholder}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      );
    }
    return (
      <input
        ref={inputRef}
        className="jp-edit-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") cancel();
          if (e.key === "Enter") commit();
        }}
        placeholder={placeholder}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    );
  }

  return (
    <Tag
      className={`jp-editable ${className}`}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setEditing(true);
      }}
      title="Click to edit"
      data-testid={testId}
    >
      {value || (
        <span style={{ opacity: 0.5, fontStyle: "italic" }}>{placeholder || "(empty)"}</span>
      )}
      <span className="jp-edit-pencil" aria-hidden>✎</span>
    </Tag>
  );
}

// ===========================================================================
// ReorderControls — small ↑↓ buttons shown to admins only
// ===========================================================================
export function ReorderControls({ section, index, total, label = "" }) {
  const ctx = useContent();
  if (!ctx?.isAdmin) return null;
  return (
    <div
      className="jp-reorder"
      onClick={(e) => e.stopPropagation()}
      data-testid={`reorder-${section}-${index}`}
    >
      <button
        className="jp-reorder-btn"
        disabled={index === 0}
        onClick={(e) => {
          e.preventDefault();
          ctx.move(section, index, index - 1);
        }}
        title={`Move ${label || "item"} up`}
        data-testid={`reorder-${section}-${index}-up`}
      >
        ▲
      </button>
      <button
        className="jp-reorder-btn"
        disabled={index === total - 1}
        onClick={(e) => {
          e.preventDefault();
          ctx.move(section, index, index + 1);
        }}
        title={`Move ${label || "item"} down`}
        data-testid={`reorder-${section}-${index}-down`}
      >
        ▼
      </button>
    </div>
  );
}

// ===========================================================================
// EditableList — wraps a list/array of strings (e.g. highlights) with add/remove + edit
// ===========================================================================
export function EditableList({ path, items, placeholder = "New item", testId }) {
  const ctx = useContent();
  const { isAdmin } = ctx || {};
  const [adding, setAdding] = useState(false);
  const [newVal, setNewVal] = useState("");

  const update = (idx, val) => {
    const next = [...items];
    next[idx] = val;
    ctx.updateField(path, next);
  };
  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    ctx.updateField(path, next);
  };
  const add = () => {
    if (!newVal.trim()) {
      setAdding(false);
      return;
    }
    ctx.updateField(path, [...items, newVal.trim()]);
    setNewVal("");
    setAdding(false);
  };
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    ctx.updateField(path, next);
  };
  const moveDown = (idx) => {
    if (idx === items.length - 1) return;
    const next = [...items];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    ctx.updateField(path, next);
  };

  return (
    <>
      {items.map((h, i) => (
        <div key={`${i}-${h}`} className="jp-list-row" data-testid={testId ? `${testId}-${i}` : undefined}>
          <EditableText
            path={[...path, i]}
            value={h}
            testId={testId ? `${testId}-text-${i}` : undefined}
            className="jp-list-text"
          />
          {isAdmin && (
            <div className="jp-list-actions">
              <button
                className="jp-mini-btn"
                onClick={() => moveUp(i)}
                disabled={i === 0}
                title="Move up"
                data-testid={testId ? `${testId}-up-${i}` : undefined}
              >▲</button>
              <button
                className="jp-mini-btn"
                onClick={() => moveDown(i)}
                disabled={i === items.length - 1}
                title="Move down"
                data-testid={testId ? `${testId}-down-${i}` : undefined}
              >▼</button>
              <button
                className="jp-mini-btn danger"
                onClick={() => remove(i)}
                title="Delete"
                data-testid={testId ? `${testId}-del-${i}` : undefined}
              >×</button>
            </div>
          )}
        </div>
      ))}
      {isAdmin && (
        adding ? (
          <div className="jp-list-row">
            <input
              autoFocus
              className="jp-edit-input"
              value={newVal}
              onChange={(e) => setNewVal(e.target.value)}
              onBlur={add}
              onKeyDown={(e) => {
                if (e.key === "Enter") add();
                if (e.key === "Escape") { setNewVal(""); setAdding(false); }
              }}
              placeholder={placeholder}
              data-testid={testId ? `${testId}-new-input` : undefined}
            />
          </div>
        ) : (
          <button
            className="jp-add-btn"
            onClick={() => setAdding(true)}
            data-testid={testId ? `${testId}-add` : undefined}
          >
            + Add {placeholder.toLowerCase()}
          </button>
        )
      )}
    </>
  );
}
